import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createFacilityRequest,
  listFacilityGroups,
  listFacilityAssets,
  listFacilityRequests,
  listAssignees,
  updateFacilityRequest,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { useFavorites } from "../lib/useFavorites";
import { formatDateShort, formatFacilityNumber } from "../lib/format";
import { useFacilityNumberFormat } from "../lib/facilityNumbering";
import { FacilityConfigProvider, useFacilityOptions } from "../lib/facilityConfig";
import { FACILITY_PRIORITIES, FACILITY_RECURRENCES } from "../lib/constants";
import type { FacilityOption } from "../lib/api";
import type { CreateFacilityRequestInput, FacilityRequestRow } from "../lib/types";
import { PageBar } from "../components/Layout";
import { Avatar } from "../components/Avatar";
import {
  AssigneesField,
  RequestersField,
  type AssigneePick,
  type RequesterPick,
} from "../components/PersonPickers";
import { Modal } from "../components/Modal";
import { LocationPicker } from "../components/LocationPicker";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { FacilityRequestDetailBody } from "./FacilityRequestDetailPage";

/** Inbox (list) vs Board (Kanban) vs Calendar (scheduled maintenance by date).
 *  Persisted per browser. */
type FacilityView = "list" | "board" | "calendar";

/** Resolve a {code,label}[] value-set entry to its label, falling back to code. */
export function facilityLabel(
  options: { code: string; label: string }[],
  code: string | null | undefined,
): string {
  if (!code) return "—";
  return options.find((o) => o.code === code)?.label ?? code;
}

/** Small colored status pill for a facility work order. Resolves the label from
 *  the tenant's configured statuses (via context) so renamed/added statuses show
 *  correctly; the color class keys off the stable code. */
export function FacilityStatusBadge({ status }: { status: string }) {
  const { statuses } = useFacilityOptions();
  return (
    <span className={`badge facility-st facility-st-${status}`} title="Status">
      {facilityLabel(statuses, status)}
    </span>
  );
}

/** Priority pill for a facility work order. */
export function FacilityPriorityBadge({ priority }: { priority: string }) {
  return (
    <span className={`badge facility-pri facility-pri-${priority}`} title="Priority">
      {facilityLabel(FACILITY_PRIORITIES, priority)}
    </span>
  );
}

/** Work Orders queue — an inbox-style split (list + routed reading pane), mirroring
 *  the ticket inbox. `/facilities` shows the list; `/facilities/:id` opens detail.
 *  Wraps the body in the value-set provider so the whole queue (badges, filters,
 *  board columns, move selects) reads the tenant's configured statuses/types. */
export function FacilitiesPage() {
  return (
    <FacilityConfigProvider>
      <FacilitiesPageInner />
    </FacilityConfigProvider>
  );
}

function FacilitiesPageInner() {
  const navigate = useNavigate();
  const { id: selectedId } = useParams();
  const cfg = useFacilityOptions();

  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [category, setCategory] = useState("");
  const [woType, setWoType] = useState("");
  // Group is a client-side convenience filter (the list is already
  // group-visibility-scoped server-side). "" = all, "none" = unassigned.
  const [group, setGroup] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  // View preference (List split ↔ Kanban board), remembered across visits.
  const [view, setView] = useState<FacilityView>(() => {
    const v = localStorage.getItem("ac.facilities.view");
    return v === "board" || v === "calendar" ? v : "list";
  });
  useEffect(() => {
    localStorage.setItem("ac.facilities.view", view);
  }, [view]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, loading, error, reload } = useApi(
    (signal) =>
      listFacilityRequests(
        {
          ...(debounced ? { q: debounced } : {}),
          ...(status ? { status } : {}),
          ...(priority ? { priority } : {}),
          ...(category ? { category } : {}),
        },
        signal,
      ),
    [debounced, status, priority, category],
  );

  const groupsApi = useApi((signal) => listFacilityGroups(signal), []);
  const groups = groupsApi.data?.groups ?? [];

  const { isFavorite } = useFavorites();
  const [favOnly, setFavOnly] = useState(false);

  // wo_type + group + favorites aren't server filter params — narrow client-side.
  const requests = useMemo(() => {
    let rows = data?.requests ?? [];
    if (woType) rows = rows.filter((r) => r.wo_type === woType);
    if (group === "none") rows = rows.filter((r) => !r.group_id);
    else if (group) rows = rows.filter((r) => r.group_id === group);
    if (favOnly) rows = rows.filter((r) => isFavorite("facility", r.id));
    return rows;
  }, [data, woType, group, favOnly, isFavorite]);

  const hasSelection = !!selectedId;
  const filtersActive = !!(debounced || status || priority || category || woType || group || favOnly);

  function openRequest(id: string) {
    navigate(`/facilities/${id}`);
  }

  // Board cards open the work order in a dialog (overlay) so the board stays put;
  // the list still opens into its reading pane via openRequest.
  const [dialogId, setDialogId] = useState<string | null>(null);

  return (
    <>
      <PageBar
        crumbs="Work Orders"
        actions={
          <>
            <div className="seg" role="group" aria-label="Work order view">
              <button
                type="button"
                className={`seg-btn${view === "list" ? " active" : ""}`}
                aria-pressed={view === "list"}
                onClick={() => setView("list")}
              >
                List
              </button>
              <button
                type="button"
                className={`seg-btn${view === "board" ? " active" : ""}`}
                aria-pressed={view === "board"}
                onClick={() => {
                  setView("board");
                  // The board is full-width and only renders when nothing is
                  // selected — clear any open work order so the switch takes effect.
                  if (selectedId) navigate("/facilities");
                }}
              >
                Board
              </button>
              <button
                type="button"
                className={`seg-btn${view === "calendar" ? " active" : ""}`}
                aria-pressed={view === "calendar"}
                onClick={() => {
                  setView("calendar");
                  if (selectedId) navigate("/facilities");
                }}
              >
                Calendar
              </button>
            </div>
            <button className="btn primary" onClick={() => setShowCreate(true)}>
              + New work order
            </button>
          </>
        }
      />
      {/* Board (Kanban) view — full-width, grouped by status, drag to change
          status. Shown only when no work order is open; opening one falls
          through to the list split so the detail pane reads normally. */}
      {view === "board" && !hasSelection ? (
        <FacilityBoard statuses={cfg.statuses} requests={requests} loading={loading} error={error} reload={reload} onOpen={setDialogId} />
      ) : view === "calendar" && !hasSelection ? (
        <FacilityCalendar requests={requests} loading={loading} error={error} reload={reload} onOpen={setDialogId} />
      ) : (
      <div className={`inbox${hasSelection ? " has-selection" : ""}`}>
        <section className="inbox-list" aria-label="Work order queue">
          <div className="inbox-list-head">
            <div className="inbox-search">
              <span className="search-icon" aria-hidden="true">
                <SearchGlyph />
              </span>
              <input
                type="text"
                className="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search title or number…"
                aria-label="Search work orders"
              />
            </div>
            <div className="inbox-filters facility-filters" role="group" aria-label="Filters">
              <button
                type="button"
                className={`inbox-filter${favOnly ? " active" : ""}`}
                onClick={() => setFavOnly((v) => !v)}
                title="Starred work orders"
              >
                ★ Favorites
              </button>
              <select
                className="inbox-priority"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="">Any status</option>
                {cfg.statuses.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.label}
                  </option>
                ))}
              </select>
              <select
                className="inbox-priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                aria-label="Filter by priority"
              >
                <option value="">Any priority</option>
                {FACILITY_PRIORITIES.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.label}
                  </option>
                ))}
              </select>
              <select
                className="inbox-priority"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="">Any category</option>
                {cfg.categories.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
              <select
                className="inbox-priority"
                value={woType}
                onChange={(e) => setWoType(e.target.value)}
                aria-label="Filter by work order type"
              >
                <option value="">Any type</option>
                {cfg.woTypes.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
              {groups.length > 0 ? (
                <select
                  className="inbox-priority"
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                  aria-label="Filter by group"
                >
                  <option value="">Any group</option>
                  <option value="none">Unassigned</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
          </div>

          <div className="inbox-list-body">
            {loading ? (
              <Loading label="Loading work orders…" />
            ) : error ? (
              <ErrorState error={error} onRetry={reload} />
            ) : requests.length === 0 ? (
              <EmptyState>
                {filtersActive
                  ? "No work orders match the current filters."
                  : "No work orders yet. Create the first one to get started."}
              </EmptyState>
            ) : (
              <FacilityInboxList
                requests={requests}
                selectedId={selectedId}
                onOpen={openRequest}
              />
            )}
          </div>

          {requests.length > 0 ? (
            <div className="inbox-list-foot muted">
              {requests.length} work order{requests.length === 1 ? "" : "s"}
            </div>
          ) : null}
        </section>

        <section className="inbox-read" aria-label="Work order detail">
          {selectedId ? (
            <div className="inbox-read-inner">
              <button className="btn ghost small inbox-back" onClick={() => navigate("/facilities")}>
                ← Back to queue
              </button>
              <FacilityRequestDetailBody
                key={selectedId}
                id={selectedId}
                onDeleted={() => {
                  reload();
                  navigate("/facilities");
                }}
                onChanged={reload}
              />
            </div>
          ) : (
            <div className="inbox-read-empty">
              <WrenchGlyph />
              <h2>Select a work order</h2>
              <p className="muted">
                Pick a request from the queue to update its properties, run the procedure
                checklist, post comments, and attach photos.
              </p>
            </div>
          )}
        </section>
      </div>
      )}

      {showCreate ? (
        <CreateWorkOrderModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            reload();
            navigate(`/facilities/${id}`);
          }}
        />
      ) : null}

      {dialogId ? (
        <Modal title="Work order" onClose={() => setDialogId(null)} wide>
          <FacilityRequestDetailBody
            id={dialogId}
            onDeleted={() => {
              setDialogId(null);
              reload();
            }}
            onChanged={reload}
          />
        </Modal>
      ) : null}
    </>
  );
}

// ===========================================================================
// Kanban board view — work orders grouped into status columns from
// FACILITY_STATUSES. Drag a card onto a column (or use the per-card <select>)
// to change its status via PATCH { status }. Reuses the project board's
// `.board-*` styles, mirroring the ticket board.
// ===========================================================================

function FacilityBoard({
  statuses,
  requests,
  loading,
  error,
  reload,
  onOpen,
}: {
  statuses: FacilityOption[];
  requests: FacilityRequestRow[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  onOpen: (id: string) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const moveMut = useMutation((vars: { id: string; status: string }) =>
    updateFacilityRequest(vars.id, { status: vars.status }),
  );

  const handleDrop = useCallback(
    async (id: string, statusCode: string, currentStatus: string) => {
      setDragId(null);
      if (statusCode === currentStatus) return;
      setMovingId(id);
      await moveMut.run({ id, status: statusCode });
      setMovingId(null);
      reload();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moveMut.run, reload],
  );

  if (loading) return <Loading label="Loading board…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const columns = statuses;
  const known = new Set(columns.map((c) => c.code));
  const byStatus = (code: string) => requests.filter((r) => r.status === code);
  const orphanCount = requests.filter((r) => !known.has(r.status)).length;

  return (
    <div className="board-content">
      {moveMut.error ? (
        <Alert kind="error">Couldn't move the work order: {moveMut.error}</Alert>
      ) : null}
      <div className="board">
        {columns.map((col, idx) => (
          <FacilityColumn
            key={col.code}
            column={col}
            // Surface orphan-status work orders in the leftmost column so they're reachable.
            requests={idx === 0 ? [...byStatus(col.code), ...requests.filter((r) => !known.has(r.status))] : byStatus(col.code)}
            dragId={dragId}
            movingId={movingId}
            onDragState={setDragId}
            onDrop={handleDrop}
            onOpen={onOpen}
            note={idx === 0 && orphanCount > 0 ? `Includes ${orphanCount} with other statuses` : null}
          />
        ))}
      </div>
    </div>
  );
}

function FacilityColumn({
  column,
  requests,
  dragId,
  movingId,
  onDragState,
  onDrop,
  onOpen,
  note,
}: {
  column: { code: string; label: string; color?: string | null };
  requests: FacilityRequestRow[];
  dragId: string | null;
  movingId: string | null;
  onDragState: (id: string | null) => void;
  onDrop: (id: string, statusCode: string, currentStatus: string) => void;
  onOpen: (id: string) => void;
  note: string | null;
}) {
  const [dropOver, setDropOver] = useState(false);
  const depth = useRef(0);

  function handleDragEnter(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    depth.current += 1;
    setDropOver(true);
  }
  function handleDragLeave() {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDropOver(false);
  }
  function handleDragOver(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }
  function handleDrop(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    depth.current = 0;
    setDropOver(false);
    const payload = e.dataTransfer.getData("text/plain");
    if (!payload) return;
    const [id, currentStatus] = payload.split("|");
    if (id) onDrop(id, column.code, currentStatus ?? "");
  }

  // Stable accent per status code so each column reads distinctly.
  const accents = ["var(--st-open)", "var(--st-progress)", "var(--st-done)", "var(--accent)", "var(--brand-2)"];
  let h = 0;
  for (let i = 0; i < column.code.length; i++) h = (h * 31 + column.code.charCodeAt(i)) >>> 0;
  const colAccent = column.color || accents[h % accents.length];

  return (
    <section
      className={`board-col${dropOver ? " col-dropover" : ""}`}
      aria-label={column.label}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <header className="board-col-head">
        <span className="col-swatch" style={{ ["--col-accent" as string]: colAccent }} aria-hidden="true" />
        <span className="col-name">{column.label}</span>
        <span className="grow" />
        <span className="count">{requests.length}</span>
      </header>
      {note ? <div className="board-col-wip">{note}</div> : null}
      <div className="board-col-body">
        {requests.length === 0 ? (
          <p className="muted board-empty">No work orders</p>
        ) : (
          requests.map((r) => (
            <FacilityCard
              key={r.id}
              request={r}
              dragging={dragId === r.id}
              moving={movingId === r.id}
              onDragState={onDragState}
              onOpen={() => onOpen(r.id)}
            />
          ))
        )}
      </div>
    </section>
  );
}

function FacilityCard({
  request,
  dragging,
  moving,
  onDragState,
  onOpen,
}: {
  request: FacilityRequestRow;
  dragging: boolean;
  moving: boolean;
  onDragState: (id: string | null) => void;
  onOpen: () => void;
}) {
  const numFmt = useFacilityNumberFormat();
  const rail =
    {
      urgent: "var(--st-blocked)",
      high: "var(--st-progress)",
      normal: "var(--st-open)",
      low: "var(--st-closed)",
    }[(request.priority ?? "").toLowerCase()] ?? "var(--st-closed)";

  return (
    <article
      className={`board-card${moving ? " busy" : ""}${dragging ? " card-dragging" : ""}`}
      style={{ ["--card-rail" as string]: rail }}
      draggable
      onDragStart={(e) => {
        // Don't start a drag when the gesture begins on an interactive control.
        if ((e.target as HTMLElement).closest("select, button, input, a, option")) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData("text/plain", `${request.id}|${request.status}`);
        e.dataTransfer.effectAllowed = "move";
        onDragState(request.id);
      }}
      onDragEnd={() => onDragState(null)}
    >
      <span className="board-card-origin mono faint">#{formatFacilityNumber(request.number, numFmt)}</span>
      <button className="board-card-title" onClick={onOpen} title="Open work order">
        {request.title}
      </button>
      <div className="board-card-foot">
        <FacilityPriorityBadge priority={request.priority} />
        <span className="grow" />
        <Avatar name={request.assignee_name ?? undefined} size="sm" />
      </div>
    </article>
  );
}

// ===========================================================================
// Calendar view — scheduled maintenance laid out on a month grid by each work
// order's Scheduled date (falling back to its Due date). Click a chip to open
// the work order. Full-width like the board; shown only when nothing is open.
// ===========================================================================

const FAC_CAL_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const FAC_CAL_MAX_CHIPS = 3;

/** yyyy-mm-dd in LOCAL time — the bucket key for a day cell. */
function facCalDayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
/** Chip accent by priority (mirrors the board card rail). */
function facCalPriorityColor(priority: string): string {
  return (
    {
      urgent: "var(--st-blocked)",
      high: "var(--st-progress)",
      normal: "var(--st-open)",
      low: "var(--st-closed)",
    }[(priority ?? "").toLowerCase()] ?? "var(--st-closed)"
  );
}
/** All occurrence day-keys for a work order within [rangeStart, rangeEnd].
 *  A non-recurring order yields just its anchor day (if in range); a recurring
 *  one (the preventive Recurrence setting) steps forward from its anchor by the
 *  cadence, so the calendar shows every projected occurrence — e.g. a weekly
 *  filter change lands on the same weekday each week. Bounded for safety. */
function facCalOccurrences(
  anchorSec: number,
  recurrence: string | null | undefined,
  rangeStart: Date,
  rangeEnd: Date,
): string[] {
  const out: string[] = [];
  const cadence = (recurrence ?? "none").toLowerCase();
  const d = new Date(anchorSec * 1000);
  d.setHours(0, 0, 0, 0);
  if (cadence === "none" || cadence === "") {
    if (d >= rangeStart && d <= rangeEnd) out.push(facCalDayKey(d));
    return out;
  }
  const step = (dt: Date) => {
    switch (cadence) {
      case "daily": dt.setDate(dt.getDate() + 1); break;
      case "weekly": dt.setDate(dt.getDate() + 7); break;
      case "monthly": dt.setMonth(dt.getMonth() + 1); break;
      case "quarterly": dt.setMonth(dt.getMonth() + 3); break;
      case "yearly": dt.setFullYear(dt.getFullYear() + 1); break;
      default: dt.setDate(dt.getDate() + 1);
    }
  };
  let guard = 0;
  while (d < rangeStart && guard < 4000) { step(d); guard++; }
  while (d <= rangeEnd && guard < 4000) { out.push(facCalDayKey(d)); step(d); guard++; }
  return out;
}

function FacilityCalendar({
  requests,
  loading,
  error,
  reload,
  onOpen,
}: {
  requests: FacilityRequestRow[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  onOpen: (id: string) => void;
}) {
  const numFmt = useFacilityNumberFormat();
  const [mode, setMode] = useState<"month" | "week" | "day">(
    () => {
      const v = localStorage.getItem("ac.facilities.calmode");
      return v === "week" || v === "day" ? v : "month";
    },
  );
  useEffect(() => localStorage.setItem("ac.facilities.calmode", mode), [mode]);
  const [hideCompleted, setHideCompleted] = useState(
    () => localStorage.getItem("ac.facilities.calhidedone") === "1",
  );
  useEffect(() => localStorage.setItem("ac.facilities.calhidedone", hideCompleted ? "1" : "0"), [hideCompleted]);
  const [cursor, setCursor] = useState(() => new Date());
  const todayKey = facCalDayKey(new Date());

  // Day cells + a header label for the current mode (month grid / week row / day).
  const { cells, rangeLabel, showWeekdays } = useMemo(() => {
    const cell = (date: Date, inMonth: boolean) => ({ date, key: facCalDayKey(date), inMonth });
    if (mode === "day") {
      const d = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
      return {
        cells: [cell(d, true)],
        showWeekdays: false,
        rangeLabel: d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }),
      };
    }
    if (mode === "week") {
      const ws = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - cursor.getDay());
      const out = Array.from({ length: 7 }, (_, i) => cell(new Date(ws.getFullYear(), ws.getMonth(), ws.getDate() + i), true));
      const we = new Date(ws.getFullYear(), ws.getMonth(), ws.getDate() + 6);
      return {
        cells: out,
        showWeekdays: true,
        rangeLabel: `${ws.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${we.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`,
      };
    }
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const gs = new Date(year, month, 1 - new Date(year, month, 1).getDay());
    const out = Array.from({ length: 42 }, (_, i) => {
      const date = new Date(gs.getFullYear(), gs.getMonth(), gs.getDate() + i);
      return cell(date, date.getMonth() === month);
    });
    return { cells: out, showWeekdays: true, rangeLabel: cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" }) };
  }, [cursor, mode]);

  // Bucket dated work orders, projecting recurring (preventive) orders onto every
  // occurrence within the visible range so the Recurrence setting is reflected.
  const byDay = useMemo(() => {
    const map: Record<string, FacilityRequestRow[]> = {};
    if (!cells.length) return map;
    const rs = new Date(cells[0]!.date); rs.setHours(0, 0, 0, 0);
    const re = new Date(cells[cells.length - 1]!.date); re.setHours(23, 59, 59, 999);
    for (const r of requests) {
      if (hideCompleted && r.status === "done") continue;
      const when = r.scheduled_for ?? r.due_date;
      if (!when) continue;
      // Only the OPEN order projects its recurring schedule forward. A completed
      // occurrence shows once on its own date — completing one instance never
      // marks the whole series done across the calendar.
      const project = r.status === "done" ? "none" : r.recurrence;
      for (const key of facCalOccurrences(when, project, rs, re)) {
        (map[key] ??= []).push(r);
      }
    }
    return map;
  }, [requests, cells, hideCompleted]);

  const datedCount = useMemo(
    () => requests.filter((r) => r.scheduled_for || r.due_date).length,
    [requests],
  );
  // Per-mode chip cap: more room in week, unlimited in day.
  const maxChips = mode === "month" ? FAC_CAL_MAX_CHIPS : mode === "week" ? 8 : 999;

  function stepCursor(dir: -1 | 1) {
    setCursor((c) => {
      const d = new Date(c);
      if (mode === "month") d.setMonth(d.getMonth() + dir);
      else if (mode === "week") d.setDate(d.getDate() + 7 * dir);
      else d.setDate(d.getDate() + dir);
      return d;
    });
  }

  if (loading) return <Loading label="Loading calendar…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const chip = (r: FacilityRequestRow) => (
    <button
      key={r.id}
      type="button"
      className="cal-chip"
      style={{ ["--chip-c" as string]: facCalPriorityColor(r.priority) }}
      title={`#${formatFacilityNumber(r.number, numFmt)} ${r.title} — open work order`}
      onClick={() => onOpen(r.id)}
    >
      {r.title}
    </button>
  );

  return (
    <div className="board-content">
      <div className="card">
        <div className="card-head cal-head">
          <h3>{rangeLabel}</h3>
          {/* flex:1 spacer pushes the controls (Hide completed / view / nav) to
              the right — .grow has no rule inside .cal-head, so set it inline. */}
          <span style={{ flex: 1 }} />
          <label className="toggle" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: 10, fontSize: "0.85rem" }}>
            <input type="checkbox" checked={hideCompleted} onChange={(e) => setHideCompleted(e.target.checked)} />
            <span>Hide completed</span>
          </label>
          <div className="seg" role="group" aria-label="Calendar range" style={{ marginRight: 8 }}>
            {(["month", "week", "day"] as const).map((m) => (
              <button
                key={m}
                type="button"
                className={`seg-btn${mode === m ? " active" : ""}`}
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
              >
                {m[0]!.toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>
          <div className="cal-nav">
            <button type="button" className="btn small" aria-label="Previous" onClick={() => stepCursor(-1)}>‹</button>
            <button type="button" className="btn small" onClick={() => setCursor(new Date())}>Today</button>
            <button type="button" className="btn small" aria-label="Next" onClick={() => stepCursor(1)}>›</button>
          </div>
        </div>
        <div className="card-body">
          {datedCount === 0 ? (
            <p className="hint" style={{ marginTop: 0 }}>
              No scheduled work orders yet — set a Scheduled or Due date on a work order to see it here.
            </p>
          ) : null}
          {mode === "day" ? (
            // Agenda: every work order scheduled for the day, in full.
            <div className="cal-agenda">
              {(byDay[cells[0]!.key] ?? []).length === 0 ? (
                <p className="muted">Nothing scheduled for this day.</p>
              ) : (
                <div className="cal-chips">{(byDay[cells[0]!.key] ?? []).map(chip)}</div>
              )}
            </div>
          ) : (
            <div className="cal" role="grid" aria-label={`${rangeLabel} calendar`}>
              {showWeekdays ? (
                <div className="cal-weekdays" role="row">
                  {FAC_CAL_WEEKDAYS.map((w) => (
                    <div key={w} className="cal-weekday" role="columnheader">{w}</div>
                  ))}
                </div>
              ) : null}
              <div className="cal-grid" style={mode === "week" ? { gridAutoRows: "minmax(220px, auto)" } : undefined}>
                {cells.map((c) => {
                  const list = byDay[c.key] ?? [];
                  const shown = list.slice(0, maxChips);
                  const extra = list.length - shown.length;
                  const isToday = c.key === todayKey;
                  return (
                    <div
                      key={c.key}
                      role="gridcell"
                      className={`cal-cell${c.inMonth ? "" : " muted"}${isToday ? " today" : ""}`}
                    >
                      <div className="cal-cell-num">
                        <span className={isToday ? "cal-today-dot" : ""}>{c.date.getDate()}</span>
                      </div>
                      <div className="cal-chips">
                        {shown.map(chip)}
                        {extra > 0 ? <span className="cal-more">+{extra} more</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Keyboard-navigable list of compact work-order rows (↑/↓ to move, Enter to open). */
function FacilityInboxList({
  requests,
  selectedId,
  onOpen,
}: {
  requests: FacilityRequestRow[];
  selectedId: string | undefined;
  onOpen: (id: string) => void;
}) {
  const listRef = useRef<HTMLUListElement>(null);

  function onKeyDown(e: KeyboardEvent<HTMLLIElement>, idx: number) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = e.key === "ArrowDown" ? idx + 1 : idx - 1;
      const rows = listRef.current?.querySelectorAll<HTMLLIElement>("li.inbox-row");
      rows?.[Math.max(0, Math.min(rows.length - 1, next))]?.focus();
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen(requests[idx]!.id);
    }
  }

  return (
    <ul className="inbox-rows" ref={listRef}>
      {requests.map((r, idx) => (
        <FacilityInboxRow
          key={r.id}
          row={r}
          selected={r.id === selectedId}
          onOpen={() => onOpen(r.id)}
          onKeyDown={(e) => onKeyDown(e, idx)}
        />
      ))}
    </ul>
  );
}

function FacilityInboxRow({
  row,
  selected,
  onOpen,
  onKeyDown,
}: {
  row: FacilityRequestRow;
  selected: boolean;
  onOpen: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLLIElement>) => void;
}) {
  const numFmt = useFacilityNumberFormat();
  const { woTypes, campuses, buildings, rooms, labelOf } = useFacilityOptions();
  const locationText = [
    row.campus ? labelOf(campuses, row.campus) : "",
    row.building ? labelOf(buildings, row.building) : "",
    row.location ? labelOf(rooms, row.location) : "",
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <li
      className={`inbox-row${selected ? " selected" : ""}`}
      tabIndex={0}
      role="button"
      aria-current={selected ? "true" : undefined}
      aria-label={`Work order: ${row.title}`}
      onClick={onOpen}
      onKeyDown={onKeyDown}
    >
      <Avatar name={row.assignee_name ?? undefined} size="md" />
      <div className="inbox-row-main">
        <div className="inbox-row-top">
          <span className="inbox-row-subject">{row.title}</span>
          <span className="inbox-row-num mono faint">#{formatFacilityNumber(row.number, numFmt)}</span>
        </div>
        <div className="inbox-row-snip">
          <span className="faint">{row.assignee_name || "Unassigned"}</span>
          {locationText ? <span aria-hidden="true"> · </span> : null}
          {locationText ? <span className="faint">{locationText}</span> : null}
          {row.due_date ? <span aria-hidden="true"> · </span> : null}
          {row.due_date ? <span className="faint">Due {formatDateShort(row.due_date)}</span> : null}
        </div>
        <div className="inbox-row-meta">
          <FacilityStatusBadge status={row.status} />
          <FacilityPriorityBadge priority={row.priority} />
          <span className="pill" style={{ fontSize: "0.68rem" }} title="Type">
            {facilityLabel(woTypes, row.wo_type)}
          </span>
        </div>
      </div>
    </li>
  );
}

/** Create work-order modal. Carries every property the detail editor exposes.
 *  Recurrence is meaningful only for preventive work orders (PM auto-clones the
 *  next occurrence on completion); a hint spells that out. */
function CreateWorkOrderModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const cfg = useFacilityOptions();
  const assigneesApi = useApi((signal) => listAssignees(signal, { workType: "work_orders", field: "assignee" }), []);
  const assignees = assigneesApi.data?.assignees ?? [];
  const assetsApi = useApi((signal) => listFacilityAssets({}, signal), []);
  const assets = assetsApi.data?.assets ?? [];
  const groupsApi = useApi((signal) => listFacilityGroups(signal), []);
  const groups = groupsApi.data?.groups ?? [];

  const [title, setTitle] = useState("");
  const [groupId, setGroupId] = useState("");
  const [description, setDescription] = useState("");
  const [campus, setCampus] = useState("");
  const [building, setBuilding] = useState("");
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("normal");
  const [woType, setWoType] = useState("reactive");
  const [recurrence, setRecurrence] = useState("none");
  const [dueDate, setDueDate] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [assetId, setAssetId] = useState("");
  const [assigneePicks, setAssigneePicks] = useState<AssigneePick[]>([]);
  const [requesterPicks, setRequesterPicks] = useState<RequesterPick[]>([]);
  const [notifyRequester, setNotifyRequester] = useState(true);
  const [notifyAssignee, setNotifyAssignee] = useState(true);

  const { run, pending, error } = useMutation(createFacilityRequest);
  const isPreventive = woType === "preventive";

  // Recurrence only applies to preventive WOs; reset it if the type changes away.
  useEffect(() => {
    if (!isPreventive && recurrence !== "none") setRecurrence("none");
  }, [isPreventive, recurrence]);

  // If the tenant's configured types don't include the current selection (e.g.
  // they removed "reactive"), fall back to the first configured type.
  useEffect(() => {
    if (cfg.woTypes.length && !cfg.woTypes.some((t) => t.code === woType)) {
      setWoType(cfg.woTypes[0]!.code);
    }
  }, [cfg.woTypes, woType]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    const body: CreateFacilityRequestInput = {
      title: title.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(campus.trim() ? { campus: campus.trim() } : {}),
      ...(building.trim() ? { building: building.trim() } : {}),
      ...(location.trim() ? { location: location.trim() } : {}),
      ...(category ? { category } : {}),
      ...(priority ? { priority } : {}),
      ...(woType ? { wo_type: woType } : {}),
      ...(isPreventive && recurrence ? { recurrence } : {}),
      ...(dueDate ? { due_date: toUnix(dueDate) } : {}),
      ...(scheduledFor ? { scheduled_for: toUnix(scheduledFor) } : {}),
      ...(assetId ? { asset_id: assetId } : {}),
      ...(groupId ? { group_id: groupId } : {}),
      // Multi-sets (position 0 becomes the primary, mirrored server-side).
      assignee_ids: assigneePicks.map((a) => a.user_id),
      requesters: requesterPicks.map((r) => ({ name: r.name, email: r.email })),
      notify_requester: notifyRequester,
      notify_assignee: notifyAssignee,
    };
    const res = await run(body);
    if (res) onCreated(res.request.id);
  }

  return (
    <Modal
      title="New work order"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="create-wo-form"
            disabled={pending || !title.trim()}
          >
            {pending ? <Spinner label="Creating…" /> : "Create work order"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="create-wo-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short summary of the work…"
            autoFocus
            required
          />
        </label>

        <label className="field">
          <span className="label">Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{ fontFamily: "inherit" }}
            placeholder="Details, scope, or context…"
          />
        </label>

        {groups.length > 0 ? (
          <label className="field">
            <span className="label">Group <span className="faint">(controls who can see this work order)</span></span>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Unscoped (everyone with Facilities access)</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </label>
        ) : null}

        <LocationPicker
          campus={campus}
          building={building}
          room={location}
          onChange={({ campus: c, building: b, room: r }) => {
            setCampus(c);
            setBuilding(b);
            setLocation(r);
          }}
          disabled={pending}
        />

        <div className="row">
          <label className="field">
            <span className="label">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">—</option>
              {cfg.categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {FACILITY_PRIORITIES.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="row">
          <label className="field">
            <span className="label">Work order type</span>
            <select value={woType} onChange={(e) => setWoType(e.target.value)}>
              {cfg.woTypes.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Recurrence</span>
            <select
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value)}
              disabled={!isPreventive}
            >
              {FACILITY_RECURRENCES.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </select>
            <span className="hint">
              {isPreventive
                ? "On completion, a recurring PM auto-schedules the next occurrence."
                : "Recurrence applies to preventive work orders only."}
            </span>
          </label>
        </div>

        <div className="row">
          <label className="field">
            <span className="label">Due date</span>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Scheduled date</span>
            <input
              type="date"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
            />
          </label>
        </div>

        <div className="row">
          <label className="field">
            <span className="label">Equipment / asset</span>
            <select
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              disabled={assetsApi.loading}
            >
              <option value="">—</option>
              {assets.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.asset_tag ? `${a.asset_tag} · ${a.name}` : a.name}
                </option>
              ))}
            </select>
            {assetsApi.loading ? <span className="hint">Loading equipment…</span> : null}
          </label>
          <div className="field" />
        </div>

        <AssigneesField
          value={assigneePicks}
          options={assignees}
          loading={assigneesApi.loading}
          onChange={setAssigneePicks}
        />

        <RequestersField requesters={requesterPicks} onChange={setRequesterPicks} />

        <div className="composer-opts" style={{ marginTop: 4 }}>
          <label className="toggle" title="Email the requester(s) a confirmation">
            <input
              type="checkbox"
              checked={notifyRequester}
              onChange={(e) => setNotifyRequester(e.target.checked)}
            />
            Email requester
          </label>
          <label className="toggle" title="Email the assigned technician(s)">
            <input
              type="checkbox"
              checked={notifyAssignee}
              onChange={(e) => setNotifyAssignee(e.target.checked)}
            />
            Email assignee
          </label>
        </div>
      </form>
    </Modal>
  );
}

/** Convert a yyyy-mm-dd date input to a UNIX-seconds timestamp (local midnight). */
export function toUnix(dateStr: string): number {
  return Math.floor(new Date(`${dateStr}T00:00:00`).getTime() / 1000);
}

/** yyyy-mm-dd for a date <input> from a UNIX-seconds timestamp (or "" when null). */
export function toDateInput(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return "";
  const d = new Date(unixSeconds * 1000);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function SearchGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function WrenchGlyph() {
  return (
    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M14.7 6.3a4 4 0 0 0-5.4 5.2L3 17.8 6.2 21l6.3-6.3a4 4 0 0 0 5.2-5.4l-2.5 2.5-2.3-.3-.3-2.3 2.6-2.6Z" />
    </svg>
  );
}